import React, { Fragment } from "react";
import escapeHTML from "escape-html";
import { Text } from "slate";
import QuoteCard from "../Cards/QuoteCard/QuoteCard";
import CardIntersect from "../../assets/intersects/ReviewerCardIntersect.svg";
import QuotationMarks from "../../assets/icons/QuotationMarks.svg";


export interface Node {
  type?: string;
  children?: Node[];
  text?: string;
  bold?: boolean;
  relationTo?: string;
  value?: {
    id?: string;
    alt?: string;
    filename?: string;
    mimeType?: string;
    filesize?: number;
    width?: number;
    height?: number;
    createdAt?: Date;
    updatedAt?: Date;
    url?: string;
  };
}
interface SerializeProps {
  children: Node[];
  textColor?: string;
}

export const serialize = ({children, textColor = 'text-deep-blue'}: SerializeProps) => {
  let sectionCounter = 0;
  return children?.map((node, i) => {
    if (Text.isText(node)) {
      let text = (
        <span key={i} dangerouslySetInnerHTML={{ __html: escapeHTML(node.text) }} />
      );

      if (node.bold) {
        text = (
          <strong className="body-bold" key={i}>
            {text}
          </strong>
        );
      }

      if (node.code) {
        text = <code key={i}>{text}</code>;
      }

      if (node.italic) {
        text = <em key={i}>{text}</em>;
      }

      // Handle other leaf types here...

      return <Fragment key={i}>{text}</Fragment>;
    }

    if (!node) {
      return null;
    }

    switch (node.type) {
      case "h1":
        return (
          <h1 className="text-vibrant-green mb-[1rem]" key={i}>
            {serialize({children: node.children})}
          </h1>
        );
      case "h2":
        return (
          <h2 className={`${textColor} mb-[1rem]`} key={i}>
            {serialize({children: node.children})}
          </h2>
        );
      case "h3":
        return (
          <p
            id={`section${sectionCounter++}`}
            className={`${textColor} subtitle-2-bold mb-[1rem] anchor`}
            key={i}
          >
            {serialize({children: node.children})}
          </p>
        );
      case "h4":
        return (
          <QuoteCard
            key={`quoteCard-${i}`}
            quote={serialize({children: node.children})}
            backgroundGradient="bg-at-linear-cyan-violeta-top-to-bottom md:bg-at-circular-cyan-violeta-left-to-right"
            intersect={<CardIntersect />}
            quoteWidth="full"
            bottomRightSVG={
              <QuotationMarks className="w-[70px] h-[70px] rotate-180 bottom-7" />
            }
            topLeftSVG={<QuotationMarks className="w-[70px] h-[70px]" />}
            className="mt-[2rem] mb-[3rem]"
            classNameContent="py-[3rem] px-[2rem] md:px-[6rem]"
          />
        );
      case "quote":
        return <blockquote key={i}>{serialize({children: node.children})}</blockquote>;
      case "ul":
        return (
          <ul className="list-disc pl-5" key={i}>
            {serialize({children: node.children})}
          </ul>
        );
      case "ol":
        return <ol className="list-decimal pl-5" key={i}>{serialize({children: node.children})}</ol>;
      case "li":
        return (
          <li className={`${textColor} body mb-[0.5rem]`} key={i}>
            {serialize({children: node.children})}
          </li>
        );
      case "link":
        return (
          <a className="text-vibrant-green" href={escapeHTML(node.url)} key={i}>
            {serialize({children: node.children})}
          </a>
        );
      case "upload":
        if(node.relationTo === 'videos') {
          return (
            <div className="w-full" key={i} >
              <video controls poster={node.value.thumbnail.url} className="w-full" controlsList="nodownload">
                <source src={node.value.url} type="video/mp4" />
              </video>
            </div>
          )
        }

        return (
          <div className="flex justify-center items-center mb-[0.5rem]" key={i}>
            <img
              key={`image-${i}`}
              className="object-cover"
              src={node.value.url}
              alt={node.value.alt}
              loading="lazy"
            />
          </div>
        );
      default:
        return (
          <p className={`${textColor} body mb-[1.5rem]`} key={i}>
            {serialize({children: node.children})}
          </p>
        );
    }
  });
};
